import { type VariantProps, cva } from 'class-variance-authority'

// Séparé de button.tsx volontairement : ce fichier n'a pas de directive "use client",
// ce qui permet de l'importer dans des Server Components pour styler des <Link>
// avec buttonVariants() sans traverser de boundary client.
// Voir : https://github.com/mui/base-ui/discussions/157
const buttonVariants = cva(
  [
    'cursor-pointer',
    // Layout
    'inline-flex items-center justify-center whitespace-nowrap shrink-0',
    // Base styles
    // Le fond est peint jusqu'à la border-box : une variante sans bordure visible occupe
    // la même surface qu'une variante bordée, sans avoir à recolorer la bordure.
    'rounded-full border-2 border-transparent',
    'font-semibold select-none outline-none',
    'transition-all disabled:pointer-events-none disabled:opacity-30',
    // Icons — la taille par défaut de l'icône est portée par chaque `size` (24px, sauf xl 36 / header 20)
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
    // Focus
    'focus-visible:ring-[3px] focus-visible:ring-ring/50',
    // Invalid
    'aria-invalid:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive',
    'dark:aria-invalid:ring-destructive/40 dark:aria-invalid:border-destructive/50'
  ],
  {
    variants: {
      variant: {
        // Primary — bg bleu plein, texte blanc
        'primary-flat': 'bg-primary text-primary-foreground border-transparent hover:bg-[var(--blue-700)]',
        // Secondary — bg blanc, texte bleu, sans bordure
        'white-flat':
          'bg-background text-primary border-transparent hover:bg-[var(--blue-200)] hover:text-[var(--blue-700)]',
        // Tertiary — bg blanc, bordure bleue, texte bleu
        'primary-outline': [
          'bg-background text-primary border-primary!',
          'hover:border-[var(--blue-700)] hover:text-[var(--blue-700)] aria-expanded:bg-blue-100 data-[current=true]:bg-blue-100'
        ],
        // Outline — bg blanc, bordure grise, texte gris
        'grey-outline':
          'bg-background text-muted-foreground border-border hover:text-[var(--gray-600)] hover:border-[var(--gray-600)]',
        // Ghost — transparent, sans bordure, texte foncé
        ghost: 'bg-transparent text-foreground border-transparent hover:bg-muted',
        // Destructive
        destructive: [
          'bg-destructive/10 text-destructive border-transparent',
          'hover:bg-destructive/20 dark:bg-destructive/20',
          'focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40'
        ],
        // Link
        link: 'text-primary underline-offset-4 hover:underline border-transparent'
      },
      size: {
        // Responsive : mobile 48px / tablet + desktop 44px — icône 24px
        default: [
          'h-cta leading-none',
          'text-lg md:text-base',
          "[&_svg:not([class*='size-'])]:size-6",

          // Règle Figma : le padding plein est doublé côté sans icône pour
          // compenser l'absence de gap, créant un espacement optiquement équilibré.
          // --btn-px est l'unité de base (gap = padding icône = padding-plein / 2)
          '[--btn-px:0.6875rem]', // mobile — 11px
          'md:[--btn-px:0.625rem]', // md+ — 10px

          'gap-[var(--btn-px)]',
          '[&:has([data-icon="inline-start"])]:ps-[var(--btn-px)]',
          '[&:has([data-icon="inline-end"])]:pe-[var(--btn-px)]',
          'px-[calc(var(--btn-px)*2)]'
        ],
        // Special-XL — hero des pages : 52px fixe, icône 36px
        xl: [
          'h-[3.25rem] leading-6',
          'text-xl',
          "[&_svg:not([class*='size-'])]:size-9",
          '[--btn-px:0.5rem]', // 8px
          'gap-[var(--btn-px)]',
          '[&:has([data-icon="inline-start"])]:ps-[var(--btn-px)]',
          '[&:has([data-icon="inline-end"])]:pe-[var(--btn-px)]',
          'px-[calc(var(--btn-px)*2)]'
        ],
        // Special-XS — cas spécifiques : 34px fixe, icône 24px
        xs: [
          'h-[2.125rem] leading-none',
          'text-sm',
          "[&_svg:not([class*='size-'])]:size-6",
          '[--btn-px:0.3125rem]', // 5px
          'gap-[var(--btn-px)]',
          '[&:has([data-icon="inline-start"])]:ps-[var(--btn-px)]',
          '[&:has([data-icon="inline-end"])]:pe-[var(--btn-px)]',
          'px-[calc(var(--btn-px)*2)]'
        ],
        // Header — fige l'ancien style responsive (44/34/40, icône 20px) : le header ne doit pas changer
        header: [
          'h-input leading-5',
          'text-lg md:text-sm lg:text-base',
          "[&_svg:not([class*='size-'])]:size-5",
          '[--btn-px:0.625rem]', // SM — 10px
          'md:[--btn-px:0.4375rem]', // MD — 7px
          'lg:[--btn-px:0.5rem]', // LG — 8px
          'gap-[var(--btn-px)]',
          '[&:has([data-icon="inline-start"])]:ps-[var(--btn-px)]',
          '[&:has([data-icon="inline-end"])]:pe-[var(--btn-px)]',
          'px-[calc(var(--btn-px)*2)]'
        ],
        // Icône seule — carré, hauteur responsive du CTA, icône 24px
        icon: "size-cta flex items-center justify-center [&_svg:not([class*='size-'])]:size-6",
        // Icône seule, gabarit de `xs` : 34px fixe, icône 24px
        'icon-xs': "size-[2.125rem] flex items-center justify-center [&_svg:not([class*='size-'])]:size-6"
      }
    },
    defaultVariants: {
      variant: 'primary-flat',
      size: 'default'
    }
  }
)

type ButtonVariantsProps = VariantProps<typeof buttonVariants>

export { buttonVariants, type ButtonVariantsProps }
